import React from 'react';
import NotebookBoardTasksController from './../NotebookBoardTasksController';
import {
  DroppableProvided,
  DragDropContext,
  Droppable,
} from 'react-beautiful-dnd';
import { useNotebookBoardController } from './useNotebookBoardController';
import SectionLoader from '../NotebookBoardTasksController/Loader';
import { Flex } from '../../../Utils/styles/display';
import ThemeV2 from '../../../theme';

import Body from '../../../atomic/atoms/Body';
import { ADD_NEW_SECTION } from '../../../languages/en/notebook';
import ConfirmationModal from '../../../atomic/molecules/Notebook/ConfirmationModal';
import { BoardTitle, PlusIcon, Container } from './styles';
import { DummySection } from '../NotebookBoardTasksController/DummySection';

type NotebookBoardControllerProps = {
  withScrollableColumns?: boolean;
  containerHeight?: string;
  markTasksAsRead: (taskIds: string[]) => void;
};

export const NotebookBoardController = (
  props: NotebookBoardControllerProps,
) => {
  const {
    boardSections,
    isLoadingSections,
    onDragEnd,
    showDummySection,
    isSectionBeingCreated,
    handleSectionCreationButtonClick,
    handleSectionCreation,
    autoFocus,
    sectionTitle,
    setSectionTitle,
    sectionModalDropdownOptions,
    showModal,
    toggleModal,
    onPrimaryButtonClick,
    isSectionBeingDeleted,
    currentFocusedSection,
    currentSectionLength,
    setNewSectionCreationData,
    newSectionCreationData,
    currentFocusedSectionName,
  } = useNotebookBoardController();

  const { containerHeight, markTasksAsRead } = props;

  if (isLoadingSections) {
    return <SectionLoader />;
  }

  // TODO: Sumedha - Add an error state
  return (
    <>
      <Flex alignItems="flex-start">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId="board"
            type="COLUMN"
            direction="horizontal"
            ignoreContainerClipping={Boolean(containerHeight)}
          >
            {(provided: DroppableProvided) => (
              <Container ref={provided.innerRef} {...provided.droppableProps}>
                {boardSections.map((sectionId, index) => (
                  <NotebookBoardTasksController
                    index={index}
                    sectionId={sectionId}
                    key={sectionId}
                    isScrollable
                    isCombineEnabled={false}
                    dropProvided={provided}
                    toggleModal={toggleModal}
                    isLoading={
                      currentFocusedSection === sectionId &&
                      isSectionBeingDeleted
                    }
                    newSectionCreationData={newSectionCreationData}
                    setNewSectionCreationData={setNewSectionCreationData}
                    dummySectionTitle={sectionTitle}
                    setDummySectionTitle={setSectionTitle}
                    handleSectionCreation={handleSectionCreation}
                    isDummySectionLoading={
                      isSectionBeingCreated || isSectionBeingDeleted
                    }
                    markTasksAsRead={markTasksAsRead}
                  />
                ))}
                {provided.placeholder}
              </Container>
            )}
          </Droppable>
        </DragDropContext>
        {showDummySection ? (
          // This is the dummy section we render when the user has clicked on '+ ADD NEW SECTION' button
          <DummySection
            isSectionBeingCreated={
              isSectionBeingCreated || isSectionBeingDeleted
            }
            handleSectionCreation={handleSectionCreation}
            autoFocus={autoFocus}
            sectionTitle={sectionTitle}
            setSectionTitle={setSectionTitle}
          />
        ) : (
          <BoardTitle
            role="button"
            onClick={handleSectionCreationButtonClick}
            id="add-new-section"
          >
            <PlusIcon icon="union" color={ThemeV2.palette.gray9} size="16px" />
            <Body variant="body1Medium" margin="0 4px 0 0">
              {ADD_NEW_SECTION}
            </Body>
          </BoardTitle>
        )}
      </Flex>
      <ConfirmationModal
        isOpen={showModal}
        hasDropdown
        hasCheckbox={false}
        modalProps={{
          heading: 'Delete section?',
          // eslint-disable-next-line max-len
          bodyText: (
            <>
              {`You have ${currentSectionLength} tasks in the`}{' '}
              <b>{`${currentFocusedSectionName}`}</b> section. Where would you
              like to move them?
            </>
          ),
          primaryButtonText: 'Delete Section',
          secondaryButtonText: 'Cancel',
          primaryButtonType: 'warning',
        }}
        customDropdownOptions={sectionModalDropdownOptions}
        toggleOpen={() => {
          toggleModal(false, null);
        }}
        onPrimaryButtonClick={onPrimaryButtonClick}
        onSecondaryButtonClick={() => {
          toggleModal(false, null);
        }}
      />
    </>
  );
};

export default NotebookBoardController;
